/**
 * Interface-string translation.
 *
 *   g.lang                    - the user's language, default 'en' (the base
 *                               language = the language the code is written in).
 *                               Set by the app via g.init({ lang: 'ro' }).
 *   g.translate(text, vars?)  - translation of `text` for g.lang, with optional
 *                               {placeholder} interpolation.
 *   g.setTranslations(map)    - load the { source: translation } dictionary for
 *                               the current (non-base) language. The framework
 *                               emits ONE file per language and injects it before
 *                               app.js; the base language needs no file.
 *
 * Lookup: the SOURCE string is the key (gettext-style). On the base language, or
 * when a string is missing from the dictionary, the source is returned as-is -
 * so an untranslated string always renders in readable English, never a blank.
 *
 * Interpolation: placeholders are {name}; the WHOLE templated string is one
 * translation key (never translate sentence pieces - word order differs between
 * languages). Escape literal braces by doubling: {{ -> {  and  }} -> }, so a
 * string can display a literal "{name}". Interpolation runs on both base and
 * translated strings.
 *   g.translate('Hi {name}, use {{name}} to insert it', { name: 'Ana' })
 *     -> 'Hi Ana, use {name} to insert it'
 *
 * Plural forms (server docs: i18n.md "Plural forms"): a placeholder may carry
 * CLDR-category forms - {count|one:# apple|other:# apples} - and the value's
 * category (for the rendered string's language) picks the form; # inside a
 * form is the value, ## a literal #. Translations carry their own language's
 * categories (ro adds few, ru/pl few/many, ...).
 *   g.translate('{count|one:# apple|other:# apples}', { count: 3 }) -> '3 apples'
 *
 * Submit-for-translation: when g.settings.submitForTranslation is true and a
 * string is missing for a non-base language, the phrase is POSTed once (deduped)
 * to g.settings.translationEndpoint so the backend can register it for
 * translation. Fire-and-forget; the source is returned meanwhile.
 *
 * House rule: every INTERFACE string in our libraries/components goes through
 * g.translate() ("Loading...", "No results", aria labels, ...). Data coming
 * from the API is NEVER translated; business/system error messages (error.*)
 * AND server validation results (data.fields / data.message) arrive already
 * translated — the API/DB compose them for the request language carried by
 * the X-Genus-Lang header g.fetch attaches (i18n.md).
 */
import { settings } from './core.js';

export const DEFAULT_LANG = 'en';
const DEFAULT_ENDPOINT = '/frm/translations/register'; // the framework route (i18n.md), in every install
const SUBMIT_STORE_KEY = 'genus-i18n-submitted';

// { source: translation } for the current non-base language. Empty on the base
// language (or before a file is loaded) -> everything falls back to the source.
let dict = {};

export function setTranslations(map) {
	dict = map && typeof map === 'object' ? map : {};
}

const baseLang = () => settings.baseLang || DEFAULT_LANG;
const currentLang = () => (window.g && window.g.lang) || settings.lang || DEFAULT_LANG;

// The CLDR plural category (integer rules, the subset the framework's
// language registry needs — server docs: i18n.md "Plural forms"). The SAME
// table lives in core/classes/I18n.php, api/lib/i18n.js and
// helpers.plural_category — all four pinned by shared battery vectors;
// change one, change all. Unknown languages use the Germanic default.
export function pluralCategory(lang, value) {
	const n = Math.abs(Math.trunc(value));
	switch (lang) {
		case 'ja': case 'zh': case 'ko':
			return 'other';
		case 'fr': case 'pt': case 'hi':
			return n === 0 || n === 1 ? 'one' : 'other';
		case 'ro':
			if (n === 1) return 'one';
			return n === 0 || (n % 100 >= 2 && n % 100 <= 19) ? 'few' : 'other';
		case 'ru': case 'uk':
			if (n % 10 === 1 && n % 100 !== 11) return 'one';
			return n % 10 >= 2 && n % 10 <= 4 && !(n % 100 >= 12 && n % 100 <= 14) ? 'few' : 'many';
		case 'pl':
			if (n === 1) return 'one';
			return n % 10 >= 2 && n % 10 <= 4 && !(n % 100 >= 12 && n % 100 <= 14) ? 'few' : 'many';
		case 'cs': case 'sk':
			if (n === 1) return 'one';
			return n >= 2 && n <= 4 ? 'few' : 'other';
		case 'ar':
			if (n === 0) return 'zero';
			if (n === 1) return 'one';
			if (n === 2) return 'two';
			if (n % 100 >= 3 && n % 100 <= 10) return 'few';
			return n % 100 >= 11 ? 'many' : 'other';
		case 'he':
			if (n === 1) return 'one';
			return n === 2 ? 'two' : 'other';
		default:
			return n === 1 ? 'one' : 'other';
	}
}

const PLURAL_CATS = ['zero', 'one', 'two', 'few', 'many', 'other'];

// Render one plural construct's form list (i18n.md "Plural forms"): forms
// are `category:text` separated by `|`; the value's category picks the
// form, a missing category falls back to `other`, then to the first form.
// Inside a form `#` is the value, `##` a literal `#`; non-numeric values
// read as `other`.
function pluralForm(forms, value, lang) {
	const map = {};
	let first = null;
	for (const form of forms.split('|')) {
		const pos = form.indexOf(':');
		if (pos === -1) continue; // malformed form — skipped, never rendered
		const cat = form.slice(0, pos);
		if (!PLURAL_CATS.includes(cat)) continue;
		const text = form.slice(pos + 1);
		if (!(cat in map)) map[cat] = text;
		if (first === null) first = text;
	}
	const n = Number(value);
	const cat = Number.isFinite(n) ? pluralCategory(lang, n) : 'other';
	const text = map[cat] ?? map.other ?? first ?? '';
	return text.replace(/##|#/g, (m) => (m === '##' ? '#' : String(value)));
}

// Fill {name} from vars, render {name|cat:form|…} plural constructs and
// unescape {{ }} -> { }. Runs in one pass so an escaped brace never gets
// mistaken for a placeholder; `lang` picks the plural categories (the
// language OF THE STRING — i18n.md "Plural forms").
function interpolate(str, vars, lang) {
	if (str.indexOf('{') === -1) return str; // nothing to do (the common case)
	return str.replace(/\{\{|\}\}|\{(\w+)\|([^{}]*)\}|\{(\w+)\}/g, (match, pvar, forms, key) => {
		if (match === '{{') return '{';
		if (match === '}}') return '}';
		if (pvar) {
			return vars && Object.prototype.hasOwnProperty.call(vars, pvar)
				? pluralForm(forms, vars[pvar], lang)
				: match;
		}
		return vars && Object.prototype.hasOwnProperty.call(vars, key) ? vars[key] : match;
	});
}

// Deduped set of already-submitted phrases, mirrored to sessionStorage so a
// reload does not re-submit the same strings.
let submitted = null;
function submittedSet() {
	if (submitted) return submitted;
	submitted = new Set();
	try { JSON.parse(sessionStorage.getItem(SUBMIT_STORE_KEY) || '[]').forEach((k) => submitted.add(k)); } catch (e) {}
	return submitted;
}

function submitForTranslation(phrase, lang) {
	if (!settings.submitForTranslation) return;
	const set = submittedSet();
	const key = lang + ' ' + phrase;
	if (set.has(key)) return;
	set.add(key);
	try { sessionStorage.setItem(SUBMIT_STORE_KEY, JSON.stringify([...set])); } catch (e) {}
	if (window.g && window.g.fetch) {
		window.g.fetch(settings.translationEndpoint || DEFAULT_ENDPOINT, { phrase, lang }, { progress: false });
	}
}

export function translate(text, vars) {
	if (text == null) return text;
	let out = text;
	const lang = currentLang();
	// plural forms select by the language OF THE STRING being rendered: a
	// translated string uses the target language's categories, a
	// base/fallback string the base language's (i18n.md "Plural forms")
	let formsLang = baseLang();
	if (lang !== baseLang()) {
		if (Object.prototype.hasOwnProperty.call(dict, text)) {
			out = dict[text];
			formsLang = lang;
		} else submitForTranslation(text, lang);
	}
	return interpolate(out, vars, formsLang);
}
